import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { X, Heart, MessageCircle, Share2, Bookmark, ThumbsUp, Repeat2, Send, MoreHorizontal, Globe, MapPin, ChevronLeft, ChevronRight, Film } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { filesApi } from '../services/api';
import { useClient } from '../context/ClientContext';

// ─── Shared carousel for multi-media ───────────────────────────────
function MediaCarousel({ mediaUrls, aspect = 'square', className = '' }) {
  const [current, setCurrent] = useState(0);
  const count = mediaUrls.length;

  if (!count) return (
    <div className={`bg-gray-50 flex items-center justify-center text-gray-300 text-sm ${aspect === 'square' ? 'aspect-square' : 'aspect-video'} ${className}`}>
      No media attached
    </div>
  );

  const item = mediaUrls[current];
  const isVideo = item?.isVideo;

  return (
    <div className={`relative group ${className}`} data-testid="media-carousel">
      <div className={`bg-gray-100 overflow-hidden ${aspect === 'square' ? 'aspect-square' : 'aspect-video'}`}>
        {isVideo ? (
          <video src={item.blobUrl} className="w-full h-full object-cover" controls muted playsInline />
        ) : item.blobUrl ? (
          <img src={item.blobUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">Loading...</div>
        )}
      </div>
      {/* Navigation arrows */}
      {count > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent(p => (p - 1 + count) % count); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-4 h-4 text-gray-700" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent(p => (p + 1) % count); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-4 h-4 text-gray-700" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {mediaUrls.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
          {/* Counter badge */}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
            {current + 1}/{count}
          </div>
        </>
      )}
      {isVideo && (
        <div className="absolute top-2 left-2 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1">
          <Film className="w-3 h-3" /> Video
        </div>
      )}
    </div>
  );
}


// ─── Platform Previews ──────────────────────────────────────────────
function InstagramPreview({ post, mediaUrls, logoUrl, clientName, handles }) {
  const handle = handles?.instagram || clientName?.toLowerCase().replace(/\s+/g, '') || 'account';
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-[420px] mx-auto" data-testid="preview-instagram">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--brand-primary)' }}>
          {logoUrl ? <img src={logoUrl} alt="" className="w-7 h-7 object-contain" /> : <span className="text-white text-xs font-bold">{clientName?.charAt(0) || '?'}</span>}
        </div>
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-900">{handle}</span>
          {post.location ? (
            <span className="text-xs text-gray-500 block leading-tight">{post.location}</span>
          ) : (
            <span className="text-xs text-gray-500 block leading-tight">Social Media</span>
          )}
        </div>
        <MoreHorizontal className="w-5 h-5 text-gray-600" />
      </div>
      <MediaCarousel mediaUrls={mediaUrls} aspect="square" />
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Heart className="w-6 h-6 text-gray-800" />
          <MessageCircle className="w-6 h-6 text-gray-800" />
          <Send className="w-6 h-6 text-gray-800" />
        </div>
        <Bookmark className="w-6 h-6 text-gray-800" />
      </div>
      <div className="px-4 pb-4">
        <p className="text-sm font-semibold text-gray-900 mb-1">128 likes</p>
        <p className="text-sm text-gray-900">
          <span className="font-semibold">{handle}</span>{' '}
          {post.content || post.title}
        </p>
        {post.scheduled_date && (
          <p className="text-[10px] text-gray-400 mt-2 uppercase tracking-wider">
            {format(new Date(post.scheduled_date), 'MMMM d, yyyy')}
          </p>
        )}
      </div>
    </div>
  );
}

function FacebookPreview({ post, mediaUrls, logoUrl, clientName, handles }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-[420px] mx-auto" data-testid="preview-facebook">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--brand-primary)' }}>
          {logoUrl ? <img src={logoUrl} alt="" className="w-9 h-9 object-contain" /> : <span className="text-white text-sm font-bold">{clientName?.charAt(0) || '?'}</span>}
        </div>
        <div className="flex-1">
          <span className="text-[15px] font-semibold text-gray-900">{handles?.facebook || clientName || 'Page'}</span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {post.scheduled_date && <span>{format(new Date(post.scheduled_date), 'MMM d')}</span>}
            <span>·</span>
            <Globe className="w-3 h-3" />
          </div>
        </div>
        <MoreHorizontal className="w-5 h-5 text-gray-600" />
      </div>
      <div className="px-4 pb-3">
        <p className="text-[15px] text-gray-900">{post.content || post.title}</p>
      </div>
      {mediaUrls.length > 0 ? (
        mediaUrls.length === 1 ? (
          <MediaCarousel mediaUrls={mediaUrls} aspect="video" />
        ) : (
          // Facebook-style grid for multiple images
          <div className="relative">
            <MediaCarousel mediaUrls={mediaUrls} aspect="video" />
          </div>
        )
      ) : (
        <div className="aspect-video bg-gray-50 flex items-center justify-center text-gray-300 text-sm">No media attached</div>
      )}
      <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
        <span>24 reactions</span>
        <span>5 comments · 3 shares</span>
      </div>
      <div className="flex items-center justify-around px-2 py-1">
        <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium">
          <ThumbsUp className="w-5 h-5" /> Like
        </button>
        <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium">
          <MessageCircle className="w-5 h-5" /> Comment
        </button>
        <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium">
          <Share2 className="w-5 h-5" /> Share
        </button>
      </div>
    </div>
  );
}

function TwitterPreview({ post, mediaUrls, logoUrl, clientName, handles }) {
  const handle = handles?.twitter || `@${clientName?.toLowerCase().replace(/\s+/g, '') || 'account'}`;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-[420px] mx-auto" data-testid="preview-twitter">
      <div className="flex gap-3 p-4">
        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--brand-primary)' }}>
          {logoUrl ? <img src={logoUrl} alt="" className="w-9 h-9 object-contain" /> : <span className="text-white text-sm font-bold">{clientName?.charAt(0) || '?'}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[15px] font-bold text-gray-900">{clientName || 'Account'}</span>
            <span className="text-sm text-gray-500">{handle}</span>
            <span className="text-gray-400">·</span>
            {post.scheduled_date && <span className="text-sm text-gray-500">{format(new Date(post.scheduled_date), 'MMM d')}</span>}
          </div>
          <p className="text-[15px] text-gray-900 mt-1 whitespace-pre-wrap">{post.content || post.title}</p>
          {mediaUrls.length > 0 && (
            <div className="mt-3 rounded-2xl overflow-hidden border border-gray-200">
              <MediaCarousel mediaUrls={mediaUrls} aspect="video" />
            </div>
          )}
          <div className="flex items-center justify-between mt-3 max-w-[320px] text-gray-500">
            <button className="flex items-center gap-1 text-sm hover:text-blue-500"><MessageCircle className="w-[18px] h-[18px]" /> 12</button>
            <button className="flex items-center gap-1 text-sm hover:text-green-500"><Repeat2 className="w-[18px] h-[18px]" /> 8</button>
            <button className="flex items-center gap-1 text-sm hover:text-red-500"><Heart className="w-[18px] h-[18px]" /> 64</button>
            <button className="flex items-center gap-1 text-sm hover:text-blue-500"><Share2 className="w-[18px] h-[18px]" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkedInPreview({ post, mediaUrls, logoUrl, clientName, handles }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-[420px] mx-auto" data-testid="preview-linkedin">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--brand-primary)' }}>
          {logoUrl ? <img src={logoUrl} alt="" className="w-11 h-11 object-contain" /> : <span className="text-white text-lg font-bold">{clientName?.charAt(0) || '?'}</span>}
        </div>
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-900">{handles?.linkedin || clientName || 'Company'}</span>
          <span className="text-xs text-gray-500 block">1,247 followers</span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            {post.scheduled_date && <span>{format(new Date(post.scheduled_date), 'MMM d, yyyy')}</span>}
            <span>·</span>
            <Globe className="w-3 h-3" />
          </div>
        </div>
      </div>
      <div className="px-4 pb-3">
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{post.content || post.title}</p>
      </div>
      {mediaUrls.length > 0 && <MediaCarousel mediaUrls={mediaUrls} aspect="video" />}
      <div className="flex items-center justify-around px-4 py-2 border-t border-gray-100 text-gray-600 text-sm">
        <button className="flex items-center gap-1 px-3 py-2 hover:bg-gray-50 rounded"><ThumbsUp className="w-4 h-4" /> Like</button>
        <button className="flex items-center gap-1 px-3 py-2 hover:bg-gray-50 rounded"><MessageCircle className="w-4 h-4" /> Comment</button>
        <button className="flex items-center gap-1 px-3 py-2 hover:bg-gray-50 rounded"><Repeat2 className="w-4 h-4" /> Repost</button>
        <button className="flex items-center gap-1 px-3 py-2 hover:bg-gray-50 rounded"><Send className="w-4 h-4" /> Send</button>
      </div>
    </div>
  );
}

function GenericPreview({ post, mediaUrls, logoUrl, clientName }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-[420px] mx-auto" data-testid="preview-generic">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--brand-primary)' }}>
          {logoUrl ? <img src={logoUrl} alt="" className="w-9 h-9 object-contain" /> : <span className="text-white text-sm font-bold">{clientName?.charAt(0) || '?'}</span>}
        </div>
        <div className="flex-1">
          <span className="text-sm font-semibold text-gray-900">{clientName || 'Account'}</span>
          {post.scheduled_date && (
            <span className="text-xs text-gray-500 block">{format(new Date(post.scheduled_date), 'MMMM d, yyyy')}</span>
          )}
        </div>
      </div>
      <div className="px-4 py-3">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{post.title}</h3>
        {post.content && <p className="text-sm text-gray-700 whitespace-pre-wrap">{post.content}</p>}
      </div>
      {mediaUrls.length > 0 && (
        <div className="px-4 pb-3">
          <div className="rounded-xl overflow-hidden">
            <MediaCarousel mediaUrls={mediaUrls} aspect="video" />
          </div>
        </div>
      )}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
        {post.platform && post.platform !== 'general' && (
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-600 capitalize">{post.platform}</span>
        )}
        {post.post_type && post.post_type !== 'none' && (
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-50 text-purple-600">{post.post_type}</span>
        )}
      </div>
    </div>
  );
}

const PLATFORM_TABS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'X / Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
];

export function PostPreview({ isOpen, onClose, post }) {
  const [mediaUrls, setMediaUrls] = useState([]); // [{url, blobUrl, isVideo}]
  const [activePlatform, setActivePlatform] = useState(null);
  const { activeClient, resolvedLogoUrl } = useClient();

  const loadMedia = useCallback(async (urls) => {
    const loaded = await Promise.all(urls.map(async (url) => {
      const isVideo = /\.(mp4|mov|avi|webm)$/i.test(url) || url.includes('video');
      let blobUrl = await filesApi.get(url);
      if (!blobUrl && !url.includes('/')) {
        blobUrl = await filesApi.get(`/api/library/images/${url}/file`);
      }
      return { url, blobUrl, isVideo };
    }));
    setMediaUrls(loaded);
  }, []);

  useEffect(() => {
    if (!isOpen || !post) {
      setMediaUrls([]);
      return;
    }
    // Build URL list from media_urls or fallback to image_url
    const urls = post.media_urls?.length > 0
      ? post.media_urls
      : (post.image_url ? [post.image_url] : []);
    if (urls.length > 0) {
      loadMedia(urls);
    } else {
      setMediaUrls([]);
    }
    if (post.platform && post.platform !== 'general') {
      setActivePlatform(post.platform);
    } else {
      setActivePlatform('instagram');
    }
  }, [post, isOpen, loadMedia]);

  if (!post) return null;

  const clientName = activeClient?.name || '';
  const handles = activeClient?.social_handles || {};
  const logoUrl = resolvedLogoUrl;

  const renderPreview = () => {
    const props = { post, mediaUrls, logoUrl, clientName, handles };
    switch (activePlatform) {
      case 'instagram': return <InstagramPreview {...props} />;
      case 'facebook': return <FacebookPreview {...props} />;
      case 'twitter': return <TwitterPreview {...props} />;
      case 'linkedin': return <LinkedInPreview {...props} />;
      default: return <GenericPreview {...props} />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden bg-gray-50" data-testid="post-preview-modal">
        <DialogTitle className="sr-only">Post Preview</DialogTitle>
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Post Preview</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" data-testid="close-preview-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1 px-4 pt-3 pb-1 overflow-x-auto">
          {PLATFORM_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActivePlatform(tab.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                activePlatform === tab.value
                  ? 'text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              style={activePlatform === tab.value ? { backgroundColor: 'var(--brand-primary)' } : {}}
              data-testid={`preview-tab-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {renderPreview()}
          {mediaUrls.length > 1 && (
            <p className="text-[10px] text-center mt-2 font-medium" style={{ color: 'var(--brand-primary)' }}>
              {mediaUrls.length} media items — hover to navigate
            </p>
          )}
          <p className="text-[10px] text-gray-400 text-center mt-1">
            This is an approximate preview. Actual appearance may vary.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
